import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
  Link,
  Stack,
} from '@chakra-ui/react';
import { nextBillingDate } from '../api';
import { useQueryClient } from 'react-query';
import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { getNextBillingDate, getIdFromGid } from '../utils';
import { useGlobalContext } from '../state/GlobalContext';

const SkipPromptModal: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { loadPreviousFlowStep, loadNextFlowStep, jumpSteps, onClose } =
    useModalManagerContext();
  const { customerId, subscriptionId, authToken, customerPortalSettings } =
    useGlobalContext();
  const [isLoadingSkip, setIsLoadingSkip] = useState(false);
  const toast = useToast();

  const handleSkipButtonClick = async () => {
    toast.closeAll();
    setIsLoadingSkip(true);
    const subscription = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);
    const nextDate = getNextBillingDate(subscription as Subscription);

    try {
      await nextBillingDate(
        nextDate,
        customerId,
        getIdFromGid(subscription?.SubscriptionId),
        authToken,
        'SKIP',
      );
      setIsLoadingSkip(false);
      const newDate = nextDate.toDate().toLocaleDateString();
      toast({
        isClosable: true,
        position: 'top',
        status: 'success',
        title: t('cancelModal.skip.success', {
          date: newDate,
        }),
        variant: 'success',
      });
      queryClient.setQueryData(['subscription', customerId, subscriptionId], {
        ...subscription,
        NextBillingDate: newDate,
      });
      onClose();
    } catch (err) {
      setIsLoadingSkip(false);
      toast({
        isClosable: true,
        position: 'top',
        status: 'error',
        title: (
          <Text color="white">
            {t('cancelModal.error')}{' '}
            <Link onClick={handleSkipButtonClick}>
              {t('cancelModal.tryAgain')}
            </Link>
          </Text>
        ),
        variant: 'error',
      });
      console.error(err);
    }
  };

  const hideSkipNext =
    customerPortalSettings?.hideReschedule ||
    customerPortalSettings?.hideSkipNext;

  const hideEditNextOrderDate =
    customerPortalSettings?.hideReschedule ||
    customerPortalSettings?.hideEditNextOrderDate;

  return (
    <>
      <ModalHeader>{t('cancelModal.skip.header')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text variant="strong">
          {t(
            `cancelModal.skip.title${hideSkipNext ? 'HideSkip' : ''}${
              hideEditNextOrderDate ? 'HideEditNextOrderDate' : ''
            }`,
          )}
        </Text>
        <Text>
          {t(
            `cancelModal.skip.description${hideSkipNext ? 'HideSkip' : ''}${
              hideEditNextOrderDate ? 'HideEditNextOrderDate' : ''
            }`,
          )}
        </Text>

        <Stack spacing={4} direction="row" align="center">
          {!hideEditNextOrderDate ? (
            <Button
              onClick={loadNextFlowStep}
              disabled={isLoadingSkip}
              variant="outlinedBig"
            >
              {t('cancelModal.skip.ctaReschedule')}
            </Button>
          ) : null}

          {!hideSkipNext ? (
            <Button
              onClick={handleSkipButtonClick}
              disabled={isLoadingSkip}
              variant="outlinedBig"
              isLoading={isLoadingSkip}
            >
              {t('cancelModal.skip.ctaSkip')}
            </Button>
          ) : null}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          mr="4"
          onClick={loadPreviousFlowStep}
          disabled={isLoadingSkip}
          variant="outlinedBig"
        >
          {t('cancelModal.back')}
        </Button>
        <Button
          onClick={() => jumpSteps(1)}
          disabled={isLoadingSkip}
          variant="blackBig"
        >
          {t('cancelModal.continueWithCancellation')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default SkipPromptModal;
